import type { FavoriteView, View } from './types';
import { getFavoritePageType } from './types';
import { isMobileTimelineView } from './mobileTimeline';
import { getCurrentMonthKey, getCurrentWeekKey } from './utils';

/**
 * Build a direct Timeline URL from the user's top timeline favorite, bypassing
 * the /dashboard redirect (which may go to a calendar favorite). Falls back to
 * the current month.
 *
 * `mobile`: only consider favorites whose view has a phone layout (Days, Focus,
 * Tasks), so the Timeline tab and app launch never land on the "designed for larger screens" notice.
 */
export function getTimelinePath(
  favorites: FavoriteView[],
  { mobile = false }: { mobile?: boolean } = {},
): string {
  const sorted = [...favorites].sort((a, b) => a.position - b.position);
  const timelineFavorite = sorted.find(
    (favorite) =>
      getFavoritePageType(favorite) !== 'calendar' &&
      (!mobile || (!!favorite.view && isMobileTimelineView(favorite.view))),
  );

  if (timelineFavorite?.view && timelineFavorite.scale) {
    const params = new URLSearchParams();
    params.set('view', timelineFavorite.view);
    if (timelineFavorite.range) params.set('range', timelineFavorite.range as string);
    if (timelineFavorite.display && timelineFavorite.display !== 'both') {
      params.set('display', timelineFavorite.display);
    }
    const query = params.toString();
    const queryString = query ? `?${query}` : '';

    switch (timelineFavorite.scale) {
      case 'year':
        return `/year/${new Date().getFullYear()}${queryString}`;
      case 'week':
        return `/week/${getCurrentWeekKey()}${queryString}`;
      default:
        return `/month/${getCurrentMonthKey()}${queryString}`;
    }
  }

  return `/month/${getCurrentMonthKey()}`;
}

/**
 * Where to send a phone that `/dashboard` (the installed app's start_url) just
 * redirected to a desktop-only view: the same path the Timeline tab uses. Null
 * when nothing should change: on desktop, on a view with a phone layout, or on
 * a deep link (no `launchedFromDefault`), which keeps DesktopOnlyNotice (D8).
 */
export function getLaunchRedirectPath({
  launchedFromDefault,
  isMobile,
  view,
  favorites,
}: {
  launchedFromDefault: boolean;
  isMobile: boolean;
  view: View;
  favorites: FavoriteView[];
}): string | null {
  if (!launchedFromDefault || !isMobile || isMobileTimelineView(view)) return null;
  return getTimelinePath(favorites, { mobile: true });
}
